import isArray from 'lodash/isArray';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import type { StoreActionApi } from 'react-sweet-state';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { fetchJson } from '@atlassian/jira-fetch';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import {
	PACKAGE_NAME,
	isExpectedError,
} from '@atlassian/jira-servicedesk-getting-started-panel-common';
import type { GspState } from '@atlassian/jsm-getting-started-panel';

import type { State, ActionProps, GettingStartedPanelState } from '../../types';
import { handleOpsTeamMode } from '../handle-ops-team-mode';
import { handleUserCentricMode } from '../handle-user-centric-mode';

import { initializeState, getUrl } from '../initialize-state';

export type FetchState = () => Promise<void>;
export { getUrl };

/* These strings are sent in the attribute of the error analytics,
   so think twice before changing them, as you'll need to re-register them */
export const ErrorMessageByResponseError = {
	RESPONSE_NOT_OBJECT: 'response is not an object',
	RESPONSE_COMPLETED_ITEMS_NOT_ARRAY: 'response.completedItems is not an array',
	RESPONSE_PROPERTIES_NOT_OBJECT: 'response.properties is not an object',
	RESPONSE_PROPERTIES_USER_NOT_OBJECT: 'response.properties.user is not an object',
	RESPONSE_PROPERTIES_USER_SECTION_STATE_NOT_STRING:
		'response.properties.user.sectionState is not a string',
	FAILED_PANEL_STATE_JSON_PARSE: 'Failed to parse panelState as JSON',
} as const;

type TryInitializeStateArg = ActionProps & {
	dispatch: StoreActionApi<State>['dispatch'];
};

const tryInitializeState = async ({ dispatch, ...actionProps }: TryInitializeStateArg) => {
	const { isProjectTourSeen, isItsmProject, projectId, isItsmSampleProject, enableOpsTeamMode } =
		actionProps;

	const hasProject =
		isProjectTourSeen && isItsmProject && !isNil(projectId) && !isNil(isItsmSampleProject);

	if (
		hasProject ||
		// We will show the alert tab regardless of the project conditions.
		enableOpsTeamMode
	) {
		await dispatch(initializeState(actionProps))({
			projectId,
		});
	}
};

export const constructDataFromResponse = (response: GspState): GettingStartedPanelState['data'] => {
	try {
		if (!isObject(response)) {
			throw new Error(ErrorMessageByResponseError.RESPONSE_NOT_OBJECT);
		}

		const completedTasks = response?.completedItems;
		const properties = response?.properties;

		if (!isArray(completedTasks)) {
			throw new Error(ErrorMessageByResponseError.RESPONSE_COMPLETED_ITEMS_NOT_ARRAY);
		}
		if (!isObject(properties)) {
			throw new Error(ErrorMessageByResponseError.RESPONSE_PROPERTIES_NOT_OBJECT);
		}

		const userProperties = properties.user;
		if (!isObject(userProperties)) {
			throw new Error(ErrorMessageByResponseError.RESPONSE_PROPERTIES_USER_NOT_OBJECT);
		}

		const sectionStateString = userProperties.sectionState;
		if (sectionStateString === undefined) {
			return undefined;
		}
		if (!isString(sectionStateString)) {
			throw new Error(
				ErrorMessageByResponseError.RESPONSE_PROPERTIES_USER_SECTION_STATE_NOT_STRING,
			);
		}

		try {
			// further validation on this object to be done within the GSP component
			JSON.parse(sectionStateString);
			return response;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (parseError: any) {
			throw new Error(ErrorMessageByResponseError.FAILED_PANEL_STATE_JSON_PARSE);
		}
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		fireErrorAnalytics({
			error,
			meta: {
				id: 'fetchState_constructDataFromResponse',
				packageName: PACKAGE_NAME,
				teamName: 'itops-mithril',
			},
			attributes: {
				message: error.message,
			},
			sendToPrivacyUnsafeSplunk: true,
		});
		throw error;
	}
};

export const fetchState =
	(actionProps: ActionProps) =>
	({ getState, setState, dispatch }: StoreActionApi<State>): FetchState =>
	async () => {
		const { isGspInitialized, isSaving, data: currentData } = getState();
		const {
			baseUrl,
			cloudId,
			createAnalyticsEvent,
			enableOpsTeamMode,
			teamId,
			enableUserCentricMode,
		} = actionProps;

		// If state is being saved, do not fetch, as it could fetch old state before it has been saved.
		if (isSaving) {
			return;
		}

		// If state is initialized, fetch the state
		if (isGspInitialized) {
			try {
				let response;
				try {
					response = await fetchJson(getUrl({ baseUrl, cloudId }), {
						method: 'GET',
						mode: 'cors',
						credentials: 'include',
					});
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (error: any) {
					// 404 indicates that the checklist is not yet initialized for the user
					if (error.statusCode !== 404) {
						throw error;
					}
				}

				// If state is being saved, do not save fetched state,
				// as it will override current state that is being saved.
				if (isSaving) {
					return;
				}

				let data = response ? constructDataFromResponse(response) : undefined;

				if (enableUserCentricMode && data) {
					data = await handleUserCentricMode(data, cloudId);
				} else if (enableOpsTeamMode && teamId && data) {
					const opsAdminTaskVisibility = currentData?.properties.user['ops-admin-tasks-visibility'];

					data.properties.user['ops-admin-tasks-visibility'] = opsAdminTaskVisibility;

					data = await handleOpsTeamMode(data, teamId, cloudId);
				}

				// Skip unnecessary re-renders caused by the data reference changing
				if (!!data && isEqual(data, currentData)) {
					return;
				}

				setState({
					data,
					isGspInitialized: !!data,
				});

				fireTrackAnalytics(createAnalyticsEvent({}), `${PACKAGE_NAME}.fetchState succeeded`);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				if (isExpectedError(error)) {
					return;
				}
				fireErrorAnalytics({
					error,
					meta: {
						id: 'fetchState',
						packageName: PACKAGE_NAME,
						teamName: 'itops-mithril',
					},
					sendToPrivacyUnsafeSplunk: true,
				});
				throw error;
			}
		}

		/**
		 * If state is not initialized - try to initialize it
		 * Re-getting isGspInitialized from the getState(), because it might be changed (to false) by the code above
		 */
		const { isGspInitialized: currentIsGspInitialized } = getState();
		if (!currentIsGspInitialized) {
			tryInitializeState({
				...actionProps,
				dispatch,
			});
		}
	};
